import API_CONFIG from '@/config/api'
import { TIME_OFFSET_HEADER, timeOffsetMs } from '@/utils/timeTravel'
import { ERROR_MESSAGES, REQUEST_METHODS } from '@/constants/api'
import { maintenanceStore } from '@/services/maintenanceStore'
import { mutationLockStore } from '@/services/mutationLockStore'
import { versionStore } from '@/services/versionStore'
import storage from '@/utils/storage'
import type { RequestOptions } from '@/types'

/**
 * Did a request fail without us knowing whether the server acted on it? A
 * timeout or a dropped connection never got an answer, and a 502 or 504 is the
 * gateway giving up while the server may still be working. A change in any of
 * these may have been saved (DF-385).
 */
export const isUnknownOutcome = (error: unknown): boolean => {
  if ((error as { unknownOutcome?: boolean })?.unknownOutcome) return true
  const status = (error as { status?: number })?.status
  if (status === 502 || status === 504) return true
  return status === undefined && (error instanceof TypeError || (error instanceof Error && error.message === ERROR_MESSAGES.TIMEOUT))
}

const TRY_AGAIN_MESSAGE = 'We couldn’t save that change just now. Please try again soon.'

/** Set once a 401 has started the sign-out, so parallel calls don't all redirect. */
let signingOut = false
const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/**
 * Gate the global mutation lock to authenticated write endpoints. We
 * intentionally exclude public endpoints (login, forgot-password) because
 * those have their own form-level loading state and a full-screen modal
 * mid-login would look like a hang. Every customer-facing mutation that
 * can create a duplicate (subscription lines, holidays, payment method)
 * runs through /api/auth/, so this prefix is enough.
 */
const shouldEngageMutationLock = (method: string, endpoint: string): boolean => {
  if (!WRITE_METHODS.has(method.toUpperCase())) return false
  return endpoint.startsWith('/api/auth/')
}

class ApiClient {
  private baseURL: string
  private timeout: number
  private withCredentials: boolean

  constructor(config = API_CONFIG) {
    this.baseURL = config.baseURL
    this.timeout = typeof config.timeout === 'string' ? parseInt(config.timeout, 10) : config.timeout
    this.withCredentials = true
  }

  /**
   * Extract CSRF token from cookie
   */
  private getCsrfTokenFromCookie(): string | null {
    const cookies = document.cookie.split('; ')

    for (let cookie of cookies) {
      const [name, value] = cookie.split('=')
      if (name === 'XSRF-TOKEN' || name === 'csrf-token') {
        return decodeURIComponent(value)
      }
    }
    return null
  }

  /**
   * The session is over: clear it and send them to sign in again.
   *
   * Only called for a 401 that carried a token. A 401 without one is a failed
   * sign-in, where the form's own message is what the customer needs, not a
   * redirect back to the screen they are already on.
   */
  private storedToken(): string | null {
    return (storage.getLocal('auth_token') ?? storage.getSession('auth_token')) as string | null
  }

  private hasStoredToken(): boolean {
    return !!this.storedToken()
  }

  private handleExpiredSession(): void {
    if (signingOut) return
    signingOut = true

    // Both stores, as at logout: which one holds the token depends on whether
    // "Remember me" was ticked, and a dangling one would look like a session.
    storage.removeLocal('auth_token')
    storage.removeLocal('auth_user')
    storage.removeSession('auth_token')
    storage.removeSession('auth_user')

    // A full navigation rather than react-router: this is a service with no
    // router access, and the app's in-memory state still believes it has a
    // session, so the whole thing wants rebuilding from scratch.
    if (!window.location.pathname.startsWith('/login')) {
      window.location.replace('/login?expired=1')
    }
  }

  /**
   * Get auth headers with token if available
   */
  private getAuthHeaders(): Record<string, string> {
    // Read from localStorage first, fall back to sessionStorage. Both
    // stores are valid landing spots for the auth token depending on
    // whether the customer ticked "Remember me on this device" at
    // sign-in. authService.getToken does the same lookup; this client
    // duplicates it inline rather than imports authService to avoid a
    // module-level circular dependency.
    const token = this.storedToken()
    const headers: Record<string, string> = {
      Accept: 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const csrfToken = this.getCsrfTokenFromCookie()
    if (csrfToken) {
      headers['X-XSRF-TOKEN'] = csrfToken
    }

    // Xdebug step-debugging trigger. Only sent in `vite dev` builds —
    // import.meta.env.DEV is statically false in production bundles
    // and the whole branch dead-code-eliminates so the header never
    // ships to real customers. Requires Xdebug enabled on the
    // backend (e.g. `herd debug on`) and an IDE listener on port
    // 9003. The string after the equals is just a session label;
    // any non-empty value works.
    if (import.meta.env.DEV) {
      headers['X-Xdebug-Session'] = 'PHPSTORM'
    }

    // DF-515: a tester's time-machine jump, so the backend runs at their time.
    // Always 0, so never sent, in production.
    const offset = timeOffsetMs()
    if (offset > 0) {
      headers[TIME_OFFSET_HEADER] = String(Math.floor(offset / 1000))
    }

    return headers
  }

  /**
   * Make an HTTP request
   */
  async request(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<Record<string, unknown>> {
    const {
      method = REQUEST_METHODS.GET,
      headers = {},
      body = null,
      signal = null,
      skipMutationLock = false,
      checksUnknownOutcome = false,
      withoutCustomerSession = false,
      mutationLockMessage,
      mutationLockSuccessMessage,
    } = options

    const url = `${this.baseURL}${endpoint}`
    const lockEnabled = !skipMutationLock && shouldEngageMutationLock(method, endpoint)

    const config: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(withoutCustomerSession ? { Accept: 'application/json' } : this.getAuthHeaders()),
        ...headers,
      },
    }

    if (this.withCredentials) {
      config.credentials = 'include'
    }

    if (body) {
      config.body = JSON.stringify(body)
    }

    if (signal) {
      config.signal = signal
    }

    // Engage the global mutation lock BEFORE the fetch promise is
    // awaited — synchronously. The overlay subscriber re-renders on the
    // same microtask, takes a shared body-scroll lock (see
    // bodyScrollLock), and the backdrop intercepts any further pointer
    // events. This is what closes the double-click window that the local
    // React-state `submitting` flag is too slow to catch.
    if (lockEnabled) {
      mutationLockStore.begin(mutationLockMessage)
    }

    try {
      const response = await Promise.race([
        fetch(url, config),
        new Promise<Response>((_, reject) =>
          setTimeout(
            () => reject(new Error(ERROR_MESSAGES.TIMEOUT)),
            this.timeout
          )
        ),
      ])

      // Read before the ok check, so a failing response still tells an open tab
      // that it is behind. A stale client whose calls all fail is exactly the
      // one that needs to know.
      versionStore.observed(response.headers.get('X-Frontend-Version'))

      // An expired or revoked token. Distinguished from a failed sign-in by
      // whether we sent a token at all.
      if (response.status === 401 && this.hasStoredToken() && !withoutCustomerSession) {
        this.handleExpiredSession()
      }

      // Parse response body
      const data = await response.json().catch(() => ({}))

      // The portal was taken offline for maintenance while this tab was open.
      // Flip the store so the app shows the notice rather than failing every
      // call with a generic error. Guarded on the flag, so an ordinary 503
      // (server busy) does not black out the app.
      if (response.status === 503 && (data as { maintenance?: boolean }).maintenance) {
        maintenanceStore.observed((data as { message?: string }).message)
      }

      if (!response.ok) {
        // Create error with response data for better error handling
        const error = new Error(
          data.message || `HTTP ${response.status}: ${response.statusText}`
        ) as Error & { status: number; data: Record<string, unknown> }
        error.status = response.status
        error.data = data
        throw error
      }

      if (lockEnabled) {
        mutationLockStore.succeed(mutationLockSuccessMessage ?? 'Saved')
      }

      return data
    } catch (error) {
      console.error(
        `API Request Error [${method} ${endpoint}]:`,
        error
      )
      if (lockEnabled) {
        // Validation-style failures (422/409/404) come from the user's
        // own input — the form's own error UI handles them, so don't
        // flash a generic "try again" modal on top. For anything else
        // (network blip, 500, timeout) surface the overlay error so the
        // user gets an obvious "save failed" cue instead of silence.
        const status = (error as { status?: number })?.status
        // 401 joins these: an expired session redirects to sign in, and a
        // failed sign-in is answered by the login form itself. Neither wants a
        // generic "try again soon" thrown over the top.
        const isUserInputError = status === 422 || status === 409 || status === 404 || status === 401
        if (isUserInputError || (checksUnknownOutcome && isUnknownOutcome(error))) {
          mutationLockStore.retire()
        } else {
          mutationLockStore.fail(TRY_AGAIN_MESSAGE)
        }
      }
      const handled = this.handleError(error)
      if (isUnknownOutcome(error)) {
        (handled as Error & { unknownOutcome?: boolean }).unknownOutcome = true
      }
      throw handled
    }
  }

  /**
   * GET request
   */
  get(endpoint: string, options: RequestOptions = {}): Promise<Record<string, unknown>> {
    return this.request(endpoint, {
      ...options,
      method: REQUEST_METHODS.GET,
    })
  }

  /**
   * POST request
   */
  post(
    endpoint: string,
    body: unknown,
    options: RequestOptions = {}
  ): Promise<Record<string, unknown>> {
    return this.request(endpoint, {
      ...options,
      method: REQUEST_METHODS.POST,
      body,
    })
  }

  /**
   * PUT request
   */
  put(
    endpoint: string,
    body: unknown,
    options: RequestOptions = {}
  ): Promise<Record<string, unknown>> {
    return this.request(endpoint, {
      ...options,
      method: REQUEST_METHODS.PUT,
      body,
    })
  }

  /**
   * PATCH request
   */
  patch(
    endpoint: string,
    body: unknown,
    options: RequestOptions = {}
  ): Promise<Record<string, unknown>> {
    return this.request(endpoint, {
      ...options,
      method: REQUEST_METHODS.PATCH,
      body,
    })
  }

  /**
   * DELETE request
   */
  delete(endpoint: string, options: RequestOptions = {}): Promise<Record<string, unknown>> {
    return this.request(endpoint, {
      ...options,
      method: REQUEST_METHODS.DELETE,
    })
  }

  /**
   * Handle API errors
   */
  private handleError(error: unknown): Error {
    if (error instanceof TypeError) {
      return new Error(ERROR_MESSAGES.NETWORK_ERROR)
    }
    if (error instanceof Error) {
      return error
    }
    return new Error('An unknown error occurred')
  }
}

export default new ApiClient(API_CONFIG)
